<script setup>
// Vue twin of <x-form-card> (resources/views/components/form-card.blade.php +
// App\View\Components\FormCard). It emits the same markup and class hooks and
// carries no styling of its own: components/form-card.scss styles both
// implementations. Server-rendered pages keep using <x-form-card>; the search
// page's pop-out panels use this.
//
// Both states are always fully in the DOM and the `completed` class on the root
// is the single switch between them, exactly as in Blade. The difference is who
// owns that switch: on the Blade side the vanilla handlers in app.js toggle the
// class and write into .form_card_completed_title / .form_card_summary
// directly, whereas here the parent owns which stage is open and both texts are
// bound props. So this carries neither data-form-card (app.js's discovery key,
// styled nowhere) nor data-form-card-edit, and no data-selected-title /
// data-selected-summary - those exist only so the script can read a chosen value
// back out of the DOM, which a bound prop makes unnecessary.
//
// Edit is an emit rather than a handler: reopening a stage is the parent's
// decision, since it is the parent that knows what the stages are.
import { computed, useId } from 'vue';
import SvgIcon from '@/components/SvgIcon.vue';

const props = defineProps({
    title: {
        type: String,
        required: true,
    },
    // An Icon enum value, passed through SvgIcon, which owns icon validation -
    // an unknown name logs and renders no icon.
    icon: {
        type: String,
        default: null,
    },
    // The title shown while collapsed - the chosen value where one reads better
    // than the question ("Darlington" over "Location").
    completedTitle: {
        type: String,
        default: null,
    },
    summary: {
        type: String,
        default: null,
    },
    completed: {
        type: Boolean,
        default: false,
    },
    // When set, the edit control navigates instead of emitting.
    editUrl: {
        type: String,
        default: null,
    },
    // Nullable rather than defaulting to true, matching the Blade component:
    // null means "decide from editUrl", so callers written before this prop
    // existed behave as they did. Only an explicit false is new - it withholds
    // the edit control entirely, for a collapsed card whose value cannot be
    // amended from where it is shown.
    // null is included in the type because the prop genuinely accepts it: an
    // explicit default already keeps an omitted prop as null (Vue's boolean
    // casting does not override a stated default), so this is about declaring
    // the accepted values honestly rather than about protecting the default -
    // it also keeps a deliberately passed null from tripping a dev-mode prop
    // type warning.
    editable: {
        type: [Boolean, null],
        default: null,
    },
});

defineEmits(['edit']);

const collapsedTitle = computed(() => props.completedTitle ?? props.title);

// Whether to render an edit control at all; editUrl still decides which one.
const showEdit = computed(() => props.editable ?? true);

// Referenced by the root's aria-labelledby so the card announces as a named
// group. Only the visible title span feeds the name (display: none is excluded
// from accessible-name computation), so the announced name follows the
// completed/open state automatically.
const titleId = useId();
</script>

<template>
    <div :class="['form_card', { completed }]" role="group" :aria-labelledby="titleId">
        <header class="form_card_header">
            <SvgIcon v-if="icon" :name="icon" />

            <h4 :id="titleId">
                <span class="form_card_open_title">{{ title }}</span>
                <span class="form_card_completed_title">{{ collapsedTitle }}</span>
            </h4>

            <!-- showEdit is false only when editable="false" was passed - a
                 collapsed card whose value cannot be amended from where it is
                 shown. Withheld rather than hidden in CSS, matching Blade: a
                 disabled-looking pencil that does nothing is worse than none.
                 Folded into both conditions rather than wrapped in a <template>
                 so the rendered DOM stays identical to the Blade view's. -->
            <a v-if="showEdit && editUrl" :href="editUrl" class="form_card_edit" :aria-label="`Edit ${title}`">
                <SvgIcon name="edit" />
            </a>
            <button
                v-else-if="showEdit"
                type="button"
                class="form_card_edit plain"
                :aria-label="`Edit ${title}`"
                @click="$emit('edit')"
            >
                <SvgIcon name="edit" />
            </button>
        </header>

        <!-- Always present, not wrapped in v-if, to match the Blade view: hidden
             by form-card.scss while the card is open, and empty until a summary
             is passed. -->
        <p class="form_card_summary">{{ summary }}</p>

        <div class="form_card_body">
            <slot />
        </div>
    </div>
</template>
